import { useState } from 'react';

import { useShowConsumerCampaignSettingsById, useShowStoreItem } from '@givebacks/api/store';
import { PeerDonationCredit } from 'core/Components';
import { centsToDollar } from 'core/Utils';
import { DEFAULT_BRAND_IMAGE, Modal, ModalProps, ProductCard, Stack, Text } from 'ui';

import { StoreItemForm, StoreItemFormFooter } from './forms/StoreItemForm';

type BenefitCardProps = {
  /** If given, this will be used to within the crediting section */
  campaignId?: string;

  storeId?: string;
  storeItemId?: string;
  title: string;
  description: string;
  price: number;
  image?: string;
  onClick?: () => void;
};

const BenefitCard = ({ title, description, price, image, onClick }: BenefitCardProps) => {
  return (
    <ProductCard
      title={title}
      description={description}
      price={centsToDollar(price)}
      onClick={onClick}
      img={image}
    />
  );
};

interface BenefitDetailsContentProps
  extends Pick<
    BenefitDetailsModalProps,
    'storeItemId' | 'storeId' | 'title' | 'price' | 'description' | 'campaignId'
  > {
  onClose?: () => void;
}

const BenefitDetailsContent = ({
  storeItemId,
  storeId,
  title,
  price,
  description,
  onClose,
  campaignId,
}: BenefitDetailsContentProps) => {
  const { data: storeItem } = useShowStoreItem({ uuid: storeItemId, causeId: storeId });
  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId: storeId,
    campaignId,
  });

  const showPeerCredit =
    campaignSettings?.isParticipantsEnabled || campaignSettings?.isTeamsEnabled;

  if (!storeId) return null;

  return (
    <Stack p="lg" gap="lg">
      <Stack gap="xs">
        <Text fz="xxl" fw={600}>
          {title}
        </Text>
        <Text fz="sm" fw={700} c="mint">
          {centsToDollar(price)}
        </Text>
        <Text fz="sm">{description}</Text>
        {!campaignSettings?.isCampaignReadOnly && (
          <Stack gap="lg">
            {storeItem && (
              <StoreItemForm storeId={storeId} storeItem={storeItem} withFooter={false} />
            )}
            {campaignId && showPeerCredit && (
              <PeerDonationCredit
                storeId={storeId}
                storeItemId={campaignId}
                withCheckoutButtons={false}
              />
            )}
            {storeItem && (
              <StoreItemFormFooter storeId={storeId} storeItem={storeItem} onSuccess={onClose} />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

interface BenefitDetailsModalProps
  extends Omit<ModalProps, 'onClick'>,
    Omit<BenefitCardProps, 'title' | 'onClick'> {
  itemName?: string;
}

const BenefitDetailsModal = ({
  opened,
  onClose,
  itemName,
  price,
  storeId,
  storeItemId,
  description,
  image,
  campaignId,
}: BenefitDetailsModalProps) => {
  return (
    <Modal.Root opened={opened} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.ImageHeader src={image ?? DEFAULT_BRAND_IMAGE} aspectRatio={2 / 1}>
          <Modal.CloseButton />
        </Modal.ImageHeader>
        <Modal.Body p={0}>
          <BenefitDetailsContent
            description={description}
            price={price}
            storeId={storeId}
            storeItemId={storeItemId}
            title={itemName}
            onClose={onClose}
            campaignId={campaignId}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

type BenefitDetailsModalCardProps = BenefitCardProps;

const BenefitDetailsModalCard = ({
  storeId,
  storeItemId,
  title,
  description,
  price,
  image,
  campaignId,
}: BenefitDetailsModalCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <BenefitCard
        title={title}
        description={description}
        price={price}
        image={image}
        onClick={() => setIsOpen(true)}
        campaignId={campaignId}
      />
      <BenefitDetailsModal
        storeId={storeId}
        storeItemId={storeItemId}
        image={image}
        price={price}
        itemName={title}
        description={description}
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        campaignId={campaignId}
      />
    </>
  );
};

export { BenefitCard, BenefitDetailsModalCard };
